import { useEffect, useState } from 'react'
import { api } from '../../lib/api.ts'
import type { Bootstrap } from '../../lib/catalog.ts'
import { useT } from '../../lib/i18n.tsx'
import { useWiMutation, type WorkItemDetail } from './api.ts'
import { SeletorDeItens } from './SeletorDeItens.tsx'
import { ComoIncluirNoPai } from './ComoIncluirNoPai.tsx'
import { Dropdown, EmptyBlock, StatusPill, useConfirm } from './bits.tsx'

/**
 * ITENS RELACIONADOS (SPEC-20261008-1954-itens-relacionados, pedido do
 * Cristian): "ao incluir um item possa selecionar ele ou criar, escolher qual
 * é a relação e possa remover/desvincular. Nos itens relacionados deve ser
 * possível ligar um item ao pai dele e visualizar isso como uma das relações.
 * Somente os itens filhos ficam separados com a área somente deles."
 *
 * O PAI aparece aqui como uma relação ("Filho de"), e escolher "Filho de" ao
 * incluir MOVE o item para debaixo do escolhido. O tipo de relação é sempre
 * escolhido pela pessoa — nada vem marcado. Os filhos
 * continuam na seção deles. O rascunho funciona como o do item filho: digitar
 * o título cria um item novo; a lupa escolhe itens existentes (vários de uma
 * vez, menos para o pai, que é um só).
 */

/** Uma relação que dá para escolher: tipo + sentido, ou o pai. */
type OpcaoDeRelacao =
  | { tipo: 'pai'; rotulo: string }
  | { tipo: 'relacao'; relationTypeId: string; sentido: 'forward' | 'reverse'; rotulo: string }

const CRITERIO = 'ACCEPTANCE_CRITERION_OF'

export function ItensRelacionados({
  detail,
  bootstrap,
  onOpenItem,
  rascunhoNonce,
}: {
  detail: WorkItemDetail
  bootstrap: Bootstrap
  onOpenItem: (id: string) => void
  /** Muda quando "Adicionar → Item relacionado" é escolhido: abre o rascunho. */
  rascunhoNonce: number
}) {
  const t = useT()
  const confirm = useConfirm()
  const item = detail.item

  // ------------------------------------------------------------- opções
  // "Filho de" é a relação de HIERARQUIA (pedido do Cristian, 2026-10-08: "além
  // dos tipos de relação cadastrados deve possuir uma que é Filho de"): vem
  // primeiro e faz deste item filho do escolhido
  const opcoes: OpcaoDeRelacao[] = [
    { tipo: 'pai', rotulo: 'Filho de' },
    ...(bootstrap.relationTypes ?? [])
      .filter((r) => r.code !== CRITERIO && (r as { isActive?: boolean }).isActive !== false)
      .flatMap((r): OpcaoDeRelacao[] => {
        const base = `relation.${r.code.toLowerCase()}`
        const ida: OpcaoDeRelacao = { tipo: 'relacao', relationTypeId: r.id, sentido: 'forward', rotulo: t(`${base}.forward`) }
        if (r.isSymmetric) return [ida]
        return [ida, { tipo: 'relacao', relationTypeId: r.id, sentido: 'reverse', rotulo: t(`${base}.reverse`) }]
      }),
  ]
  // o tipo de relação é ESCOLHA da pessoa: nada vem marcado
  const [opcao, setOpcao] = useState<OpcaoDeRelacao | null>(null)
  const escolhida = opcao

  // ------------------------------------------------------------- linhas
  const relacoes = detail.relations.filter((r) => r.relationTypeCode !== CRITERIO)
  const pai = item.parentWorkItemId ? (detail.breadcrumb.find((b) => b?.id === item.parentWorkItemId) ?? null) : null

  const statusLabel = (code: string) => {
    const key = `work_item_status.${code.toLowerCase()}.name`
    const label = t(key)
    return label === key ? code : label
  }

  // ------------------------------------------------------------- ações
  const removerRelacao = useWiMutation(item.id, async (relationId: string) =>
    api['work-items'][':id'].relations[':relationId'].$delete({ param: { id: item.id, relationId } }),
  )
  const desvincularDoPai = useWiMutation(item.id, async () =>
    api['work-items'][':id'].move.$post({ param: { id: item.id }, json: { parentId: null } }),
  )
  /** Liga o item `outroId` a este, pela relação escolhida. */
  const ligar = async (op: OpcaoDeRelacao, outroId: string) => {
    if (op.tipo === 'pai') {
      await api['work-items'][':id'].move.$post({ param: { id: item.id }, json: { parentId: outroId } })
      return
    }
    // "ida": este → outro; "volta": o outro → este (a relação nasce no outro)
    const [origem, destino] = op.sentido === 'forward' ? [item.id, outroId] : [outroId, item.id]
    await api['work-items'][':id'].relations.$post({
      param: { id: origem },
      json: { targetWorkItemId: destino, relationTypeId: op.relationTypeId, isRequired: false },
    })
  }
  const ligarVarios = useWiMutation(item.id, async (input: { op: OpcaoDeRelacao; ids: string[] }) => {
    for (const id of input.ids) await ligar(input.op, id)
  })
  /** Título digitado: cria o item (mesmo tipo, prioridade, empresa e área deste) e liga. */
  const criarELigar = useWiMutation(item.id, async (input: { op: OpcaoDeRelacao; titulo: string }) => {
    const inicial = bootstrap.workItemStatuses.find((s) => s.isInitial) ?? bootstrap.workItemStatuses[0]!
    const r = await api['work-items'].$post({
      json: {
        title: input.titulo,
        description: '',
        workItemTypeId: item.workItemTypeId,
        workItemStatusId: inicial.id,
        workItemPriorityId: item.workItemPriorityId,
        companyId: item.companyId,
        companyAreaId: item.companyAreaId,
        portfolioNodeId: item.portfolioNodeId,
      } as never,
    })
    const criado = (await r.json()) as { item?: { id: string }; id?: string }
    const novoId = criado.item?.id ?? criado.id
    if (novoId) await ligar(input.op, novoId)
  })

  // ------------------------------------------------------------- rascunho
  const [rascunho, setRascunho] = useState(false)
  const [titulo, setTitulo] = useState('')
  const [menuAberto, setMenuAberto] = useState(false)
  useEffect(() => {
    if (rascunhoNonce > 0) setRascunho(true)
  }, [rascunhoNonce])
  const salvar = () => {
    const texto = titulo.trim()
    if (!texto || !escolhida) return
    criarELigar.mutate({ op: escolhida, titulo: texto })
    setTitulo('')
  }

  // ------------------------------------------------------------- seletor
  const [seletorAberto, setSeletorAberto] = useState(false)
  // fora da busca: o próprio item, o pai e o que já está relacionado
  const jaLigados = new Set<string>([item.id, ...(pai ? [pai.id] : []), ...relacoes.map((r) => r.other.id)])
  const umSo = escolhida?.tipo === 'pai'
  /** "Filho de" com pai escolhido: pergunta se entra num checklist dele (SPEC-20261008-2204-filho-de-em-checklist-do-pai). */
  const [paiEscolhido, setPaiEscolhido] = useState<string | null>(null)
  const incluirNoPai = useWiMutation(item.id, async (input: { paiId: string; checklistId: string | null }) => {
    await api['work-items'][':id'].move.$post({ param: { id: item.id }, json: { parentId: input.paiId } })
    // no checklist do pai: vira também uma linha dele, apontando para este item
    if (input.checklistId) {
      await api['work-items'].checklists[':checklistId'].items.$post({
        param: { checklistId: input.checklistId },
        json: { text: '', linkedWorkItemId: item.id },
      })
    }
  })
  const relacionarMarcados = async (ids: string[]): Promise<string[]> => {
    if (!escolhida) return ids
    if (escolhida.tipo === 'pai') {
      setPaiEscolhido(ids[0] ?? null)
      return []
    }
    try {
      await ligarVarios.mutateAsync({ op: escolhida, ids })
      setRascunho(false)
      return []
    } catch {
      return ids
    }
  }

  const vazio = relacoes.length === 0 && !pai

  return (
    <div className="wi-relac">
      {/* ------------------------------------------------------- linhas */}
      <div className="wi-relac__lista">
        {pai ? (
          <div className="wi-rel__row">
            <button type="button" className="wi-rel__main" onClick={() => onOpenItem(pai.id)}>
              <span className="wi-rel__label wi-relac__pai">
                <i className="fas fa-level-up-alt" aria-hidden /> Filho de
              </span>
              <span className="wi-rel__code">{pai.code}</span>
              <span className="wi-rel__title">{pai.title}</span>
              <i className="fas fa-arrow-right" aria-hidden style={{ fontSize: 10, color: 'var(--ob-text-muted)' }} />
            </button>
            <button
              type="button"
              className="wi-rel__remove"
              aria-label="Desvincular do pai"
              title="Desvincular do pai (o item vai para a raiz)"
              onClick={() =>
                confirm.ask(`${item.code} deixa de ser filho de ${pai.code} e vai para a raiz.`, () =>
                  desvincularDoPai.mutate(undefined as never),
                )
              }
            >
              <i className="fas fa-unlink" aria-hidden style={{ fontSize: 10 }} />
            </button>
          </div>
        ) : null}
        {relacoes.map((relacao) => {
          const outro = relacao.other as typeof relacao.other & { status?: { code: string; tone: string } | null }
          return (
            <div key={relacao.relationId} className="wi-rel__row">
              <button type="button" className="wi-rel__main" onClick={() => onOpenItem(outro.id)}>
                <span className="wi-rel__label">{t(relacao.labelKey)}</span>
                <span className="wi-rel__code">{outro.code}</span>
                <span className="wi-rel__title">{outro.title}</span>
                {outro.status ? <StatusPill tone={outro.status.tone}>{statusLabel(outro.status.code)}</StatusPill> : null}
              </button>
              <button
                type="button"
                className="wi-rel__remove"
                aria-label="Desvincular"
                title="Desvincular (os dois itens continuam existindo)"
                onClick={() =>
                  confirm.ask(`A relação com ${outro.code} será removida. Os dois itens continuam existindo.`, () =>
                    removerRelacao.mutate(relacao.relationId),
                  )
                }
              >
                <i className="fas fa-unlink" aria-hidden style={{ fontSize: 10 }} />
              </button>
            </div>
          )
        })}
      </div>

      {vazio && !rascunho ? (
        <EmptyBlock
          icon="fas fa-link"
          title="Nenhum item relacionado"
          text="Ligue este item a outros — dependência, bloqueio, duplicado — ou ao pai dele."
        />
      ) : null}

      {/* ------------------------------------------------------- incluir */}
      {rascunho ? (
        <div className="wi-draft">
          <div className="wi-relac__tipo">
            <span>Relação</span>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="wi-relac__escolha"
                aria-haspopup="menu"
                aria-expanded={menuAberto}
                onClick={() => setMenuAberto((v) => !v)}
              >
                {escolhida?.rotulo ?? 'Escolha o tipo de relação'}
                <i className="fas fa-chevron-down" aria-hidden />
              </button>
              <Dropdown open={menuAberto} onClose={() => setMenuAberto(false)} align="left" width={240}>
                {opcoes.map((op) => (
                  <button
                    key={op.tipo === 'pai' ? 'pai' : `${op.relationTypeId}:${op.sentido}`}
                    type="button"
                    role="menuitem"
                    className="wi-menu__opt"
                    onClick={() => {
                      setOpcao(op)
                      setMenuAberto(false)
                    }}
                  >
                    <i
                      className={op.tipo === 'pai' ? 'fas fa-level-up-alt' : 'fas fa-link'}
                      aria-hidden
                      style={{ fontSize: 11, width: 15 }}
                    />
                    {op.rotulo}
                    {escolhida === op || (escolhida?.rotulo === op.rotulo && escolhida?.tipo === op.tipo) ? (
                      <i className="fas fa-check" aria-hidden style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ob-primary)' }} />
                    ) : null}
                  </button>
                ))}
              </Dropdown>
            </div>
          </div>
          <div className="wi-draft__row">
            <input
              className="wi-draft__input"
              autoFocus
              placeholder={
                !escolhida
                  ? 'Escolha o tipo de relação primeiro'
                  : umSo
                    ? 'Título do novo item pai…'
                    : 'Título de um novo item relacionado…'
              }
              disabled={!escolhida}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  salvar()
                }
                if (e.key === 'Escape') {
                  setRascunho(false)
                  setTitulo('')
                }
              }}
            />
            <button
              type="button"
              className="wi-draft__pick"
              aria-label="Selecionar item existente"
              title={escolhida ? 'Selecionar um Item de Trabalho existente' : 'Escolha o tipo de relação primeiro'}
              disabled={!escolhida}
              onClick={() => setSeletorAberto(true)}
            >
              <i className="fas fa-search-plus" aria-hidden style={{ fontSize: 13 }} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              className="wi-cal__save"
              style={{ width: 'auto', height: 34, padding: '0 16px', marginBottom: 0, opacity: titulo.trim() && escolhida ? 1 : 0.5 }}
              disabled={!titulo.trim() || !escolhida || criarELigar.isPending}
              onClick={salvar}
            >
              Criar e relacionar
            </button>
            <button
              type="button"
              className="ob-rte__cancel"
              onClick={() => {
                setRascunho(false)
                setTitulo('')
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="wi-relac__adicionar" onClick={() => setRascunho(true)}>
          <i className="fas fa-plus" aria-hidden /> Adicionar item relacionado
        </button>
      )}

      {/* ------------------------------------------- selecionar existentes */}
      {seletorAberto && escolhida ? (
        <SeletorDeItens
          titulo={umSo ? `${item.code} será filho de…` : `Relacionar: ${escolhida.rotulo}`}
          excluir={jaLigados}
          umSo={umSo}
          rotuloConfirmar={() => (umSo ? 'Definir como pai' : 'Relacionar')}
          onConfirmar={relacionarMarcados}
          onFechar={() => setSeletorAberto(false)}
        />
      ) : null}

      {paiEscolhido ? (
        <ComoIncluirNoPai
          paiId={paiEscolhido}
          codigoDoItem={item.code}
          incluindo={incluirNoPai.isPending}
          onFechar={() => setPaiEscolhido(null)}
          onConfirmar={(checklistId) =>
            incluirNoPai.mutate(
              { paiId: paiEscolhido, checklistId },
              {
                onSuccess: () => {
                  setPaiEscolhido(null)
                  setRascunho(false)
                },
              },
            )
          }
        />
      ) : null}

      {confirm.element}
    </div>
  )
}
